/**
 * H-6 / H-8 通知中心（admin 任意；PRD §H / AC-H-6 / AC-H-8）。
 *
 * 通知中心型別 — 對齊 BE apps/api/src/modules/notifications/。
 *
 * BE 之 listDlq 回 flat shape（含 dispatch.kind 但不含整個 dispatch row）；
 * getDispatchDetail 回 { dispatch, attempts }（無 dlq_entries）。為兼容未來
 * BE 可能 join 完整 dispatch / 附 dlq_entries，預留 optional 欄位但 UI 須做
 * defensive fallback。
 */

import { apiFetch } from './core';

export type NotificationSeverity = 'P1' | 'P2' | 'P3' | 'INFO';
export type NotificationChannelName = 'webhook' | 'sms' | 'email' | 'line';
export type NotificationAttemptStatus = 'pending' | 'sent' | 'failed' | 'acked';

export interface NotificationDispatch {
  id: string;
  severity: NotificationSeverity;
  kind: string;
  source_resource_type?: string | null;
  source_resource_id?: string | null;
  payload: unknown;
  correlation_id?: string | null;
  created_at: string;
  delivered_at: string | null;
  delivered_via: NotificationChannelName | null;
}

export interface NotificationAttempt {
  id: string;
  dispatch_id?: string;
  channel: NotificationChannelName;
  attempt_number: number;
  status: NotificationAttemptStatus;
  error_message: string | null;
  sent_at: string | null;
  acked_at: string | null;
  created_at: string;
}

/**
 * DLQ row（admin /dlq GET 用）。
 * BE 目前回 flat（含 `kind` 字串）；`dispatch` 欄位預留供未來 BE join 完整 row。
 */
export interface NotificationDlqEntry {
  id: string;
  dispatch_id: string;
  channel: NotificationChannelName;
  attempt_count: number;
  last_error: string | null;
  last_failed_at: string;
  next_retry_at: string;
  retry_count: number;
  moved_to_dlq_at: string;
  resolved_at?: string | null;
  /** BE 目前直接附 dispatch.kind 在 flat 結構上 */
  kind?: string;
  /** 未來 BE 可能 join 完整 dispatch row（forward compat） */
  dispatch?: NotificationDispatch;
}

export interface DispatchDetail {
  dispatch: NotificationDispatch;
  attempts: NotificationAttempt[];
  /** 預留 — BE getDispatchDetail 目前未回；UI defensive 顯示 */
  dlq_entries?: NotificationDlqEntry[];
}

export function listNotificationDlq(): Promise<{ items: NotificationDlqEntry[] }> {
  return apiFetch<{ items: NotificationDlqEntry[] }>('/admin/notifications/dlq', { method: 'GET' });
}

export function resolveNotificationDlq(id: string): Promise<{ resolved: boolean }> {
  return apiFetch<{ resolved: boolean }>(
    `/admin/notifications/dlq/${encodeURIComponent(id)}/resolve`,
    { method: 'POST' },
  );
}

export function getNotificationDispatch(id: string): Promise<DispatchDetail> {
  return apiFetch<DispatchDetail>(`/admin/notifications/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}
